import * as Matter from 'matter-js';
import type { SceneDef } from './types';
import { createGround, groundTopY } from './util';

export const circular: SceneDef = {
  labelKey: 'scene.circular',
  defaultPlaying: true,
  params: [
    { id: 'radius', labelKey: 'dim.radius', min: 60, max: 200, step: 10, value: 140 },
    // Lower max so the ball traces a visible loop instead of teleporting.
    { id: 'speed', labelKey: 'dim.speed', min: 0.5, max: 6, step: 0.25, value: 3 },
    { id: 'mass', labelKey: 'dim.mass', min: 0.5, max: 5, step: 0.5, value: 1 },
  ],
  build({ engine, world, dims, bounds }) {
    engine.gravity.y = 0;

    const cx = bounds.width / 2;
    // Move the center slightly above the canvas midpoint so a large orbit doesn't dip
    // into the ground.
    const cy = bounds.height * 0.42;
    const ballR = 18;
    const maxRadius = groundTopY(bounds) - cy - ballR - 12;
    const radius = Math.min(dims.radius!, maxRadius);

    const ball = Matter.Bodies.circle(cx + radius, cy, ballR, {
      mass: dims.mass!,
      frictionAir: 0,
    });
    Matter.Body.setVelocity(ball, { x: 0, y: dims.speed! });

    const string = Matter.Constraint.create({
      pointA: { x: cx, y: cy },
      bodyB: ball,
      length: radius,
      stiffness: 1,
      label: 'tension',
    });

    const ground = createGround(bounds);

    const bodies = [ground, ball];
    const constraints = [string];
    Matter.World.add(world, [...bodies, ...constraints]);

    return {
      bodies,
      constraints,
      drawOverlay(ctx) {
        ctx.save();
        ctx.strokeStyle = '#aaa';
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      },
      appliedForces(body) {
        if (body !== ball) return [];
        // Centripetal tension: T = m·v²/r, directed toward the center.
        const dx = cx - body.position.x;
        const dy = cy - body.position.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 1e-6) return [];
        const ux = dx / dist;
        const uy = dy / dist;
        const vSq = body.velocity.x * body.velocity.x + body.velocity.y * body.velocity.y;
        const t = (body.mass * vSq) / radius;
        return [
          {
            origin: { ...body.position },
            components: { x: ux * t, y: uy * t },
            labelKey: 'force.tension',
            color: '#e91e63',
          },
        ];
      },
      dispose() {
        Matter.World.remove(world, [...bodies, ...constraints]);
        engine.gravity.y = 1;
      },
    };
  },
};
